
import { IAction, createActionCreator, IActionCreator } from '../../store/actions/createActionCreator';
import { KeyFacet, stringPart, Key } from '../../models/KeyFacet';
import { IFacet, Facet } from '../../models/Facet';
import { Signal } from '../../utilities/async/Signal';
import { Patch, graft as defaultGraft, IPatch } from '../../utilities/Graft';

const operationTypeKeyFacet = new KeyFacet<{
  operationTypeId: string;
  keyFacetId: string;
}>('operation', {
  operationTypeId: stringPart(),
  keyFacetId: stringPart()
});

/**
 * @public
 */
export type OperationTypeKey<TKey extends object, TOptions extends object> = Key<{
  operationTypeId: string;
  keyFacetId: string;
}> & {
  keyType: TKey;
  optionsType: TOptions;
};

/**
 * @public
 */
export function getOperationTypeKey<TKey extends object, TOptions extends object>({
  operationTypeFacet,
  keyFacet
}: {
  operationTypeFacet: IFacet<TOptions>;
  keyFacet: KeyFacet<TKey>;
}): OperationTypeKey<TKey, TOptions> {
  return operationTypeKeyFacet.serialize({
    operationTypeId: operationTypeFacet.id,
    keyFacetId: keyFacet.id
  }) as OperationTypeKey<TKey, TOptions>;
}

/**
 * @internal
 */
export function isOperationTypeKey<TKey extends object, TOptions extends object>(
  itemKey: string | undefined
): itemKey is OperationTypeKey<TKey, TOptions> {
  return operationTypeKeyFacet.matches(itemKey);
}

/**
 * @internal
 */
export function getOperationKeyParts<TKey extends object, TOptions extends object>(
  key: OperationTypeKey<TKey, TOptions>
): {
  keyFacet: KeyFacet<TKey>,
  operationTypeFacet: Facet<TOptions>
} {
  const { keyFacetId, operationTypeId } = operationTypeKeyFacet.deserialize(key);

  const keyFacet = Facet.get(keyFacetId) as KeyFacet<TKey>;
  const operationTypeFacet = Facet.get(operationTypeId) as Facet<TOptions>;

  return {
    keyFacet,
    operationTypeFacet
  };
}

/**
 * @public
 */
export interface IQueuedOperation<TKey extends object, TOptions extends object> {
  /**
   * The for the item to which the operaton applies.
   */
  itemKey: Key<TKey>;
  /**
   * A signal to throw when the operation starts.
   */
  startSignal: Signal<void>;
  /**
   * A signal to throw when the operation completes.
   */
  completeSignal: Signal<void>;
  /**
   * The options for processing the operation on the item
   */
  options: TOptions;
}

/**
 * @public
 */
export interface IOperationState<TKey extends object, TOptions extends object> {
  queue: IQueuedOperation<TKey, TOptions>[] | undefined;
  operator: (() => void) | undefined;
}

/**
 * @public
 */
export interface IOperationMapState {
  lastOperationId: number;
  operations: {
    [operationKey: string]: IOperationState<object, object> | undefined;
  };
}

/**
 * @internal
 */
export const registerOperators: IActionCreator<'registerOperators', {
  operators: {
    [operationTypeKey: string]: () => void;
  };
}> = createActionCreator('registerOperators');

/**
 * @public
 */
export const editQueue: IActionCreator<'editQueue', {
  key: OperationTypeKey<object, object>;
  queue: IQueuedOperation<object, object>[] | Patch<IQueuedOperation<object, object>[]>;
}> = createActionCreator('editQueue');

const INITIAL_OPERATION_MAP_STATE: IOperationMapState = {
  lastOperationId: 0,
  operations: {}
};

/**
 * @internal
 */
export function operationMapReducer(state: IOperationMapState = INITIAL_OPERATION_MAP_STATE, action?: IAction | undefined, options: {
  graft?: typeof defaultGraft;
} = {}): IOperationMapState {
  const {
    graft = defaultGraft
  } = options;

  if (action) {
    if (editQueue.matches(action)) {
      const {
        key,
        queue
      } = action.payload;

      return graft(state, {
        operations: {
          [key]: {
            queue: queue
          }
        }
      });
    } else if (registerOperators.matches(action)) {
      const {
        operators
      } = action.payload;

      const operationsPatch: IPatch<IOperationMapState['operations']> = {};

      for (const operationTypeKey in operators) {
        if (Object.prototype.hasOwnProperty.call(operators, operationTypeKey)) {
          const operator = operators[operationTypeKey];

          operationsPatch[operationTypeKey] = {
            operator: operator
          };
        }
      }

      return graft(state, {
        operations: operationsPatch
      });
    }
  }

  return state;
}
